import { useEffect, useRef } from 'react';
import { useSessionStore, reportSessionExpired } from '../stores/sessionStore';
import { readSessionTimes, type TokenUser } from './tokenLifetime';
import {
  canRenewProactively,
  isSessionDead,
  renewSession,
  resetProactiveBackoff,
} from './sessionRenewal';

/** Warn when the sign-in ends within this many seconds. */
export const WARN_SECONDS = 5 * 60;
/** Renew at most this many seconds before the access token runs out. */
export const MAX_RENEW_LEAD_SECONDS = 75;
/** How often the times are checked. */
export const CHECK_INTERVAL_MS = 15_000;
/** Never start two proactive renewals closer together than this. */
export const MIN_PROACTIVE_GAP_MS = 30_000;

// Input only. Not 'scroll': pages scroll themselves (grids, focus moves), which is not the user.
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * Keeps a working user signed in and warns only when the sign-in really ends.
 *
 * Proactive renewal: shortly before the access token runs out, renew it through
 * the shared, single-flight renewSession (the same one the 401 interceptor
 * uses), so the next request does not have to fail first. It runs only when:
 *  - the tab is visible and the user has done something (mouse, keyboard,
 *    touch, wheel) since the current token arrived. An idle tab is not kept
 *    alive by this code, so Keycloak's SSO Session Idle still applies;
 *  - renewing can gain something (it does not try once Keycloak's maximum
 *    session length caps the tokens), and back-off allows it.
 *
 * Warning: from the refresh token's end (tokenLifetime.ts), never from the
 * access token. Shown when the sign-in ends within 5 minutes; the banner
 * (SessionExpiryWarning) also covers a renewal that failed for good.
 */
export function useSessionLifetime(user: TokenUser | null | undefined) {
  // The tokens seen last, to tell whether a renewal moved the end back.
  const prevRef = useRef<{ accessToken?: string; refreshToken?: string; endsAt: number | null } | null>(null);
  // The end time Keycloak would not move past (maximum session length reached).
  const cappedEndRef = useRef<number | null>(null);

  useEffect(() => {
    const store = useSessionStore.getState();
    const times = readSessionTimes(user);
    const prev = prevRef.current;

    if (times && prev && user?.refresh_token && prev.refreshToken && user.refresh_token !== prev.refreshToken) {
      // New refresh token: if its end did not move, renewing cannot extend the sign-in any more.
      cappedEndRef.current =
        times.sessionEndsAt !== null && prev.endsAt !== null && times.sessionEndsAt - prev.endsAt < 5
          ? times.sessionEndsAt
          : null;
    }
    if (times && prev?.accessToken !== user?.access_token) resetProactiveBackoff();
    prevRef.current = times
      ? { accessToken: user?.access_token, refreshToken: user?.refresh_token, endsAt: times.sessionEndsAt }
      : null;

    if (!times) {
      store.clearSessionExpiring();
      return;
    }

    const seenAt = Date.now();
    let lastActivity = 0;
    let lastProactiveAt = 0;
    let lastActivityCheck = 0;

    const capped = () => times.sessionEndsAt !== null && cappedEndRef.current === times.sessionEndsAt;

    const renewalDue = (nowS: number) => {
      if (!times.hasRefreshToken) return false;
      const end = times.sessionEndsAt;
      if (end !== null && nowS >= end) return false;
      const lead = Math.min(MAX_RENEW_LEAD_SECONDS, Math.max(5, Math.floor((times.accessLifetime ?? 300) / 4)));
      // The access token is about to run out and a new one would last longer.
      const accessDue = (end === null || times.accessExpiresAt < end - 5) && nowS >= times.accessExpiresAt - lead;
      // The sign-in itself is about to enter the warning window (long access
      // tokens): renewing resets Keycloak's idle clock, unless it is capped.
      const endDue = end !== null && !capped() && nowS >= end - WARN_SECONDS - lead;
      return accessDue || endDue;
    };

    const userIsActive = () =>
      (typeof document === 'undefined' || document.visibilityState !== 'hidden') && lastActivity >= seenAt;

    /**
     * When the user really loses access: the later of the two ends. The site
     * works for as long as the ACCESS token does, even after Keycloak's
     * session (the refresh token) has ended. The Dietech client's access
     * token lasts 48 hours while SSO Session Idle is 30 minutes, so warning
     * from the refresh token alone told people they were being signed out
     * 30 minutes into a background tab that kept working for two more days
     * (reported 2026-10-08). Renewing helps only while the refresh token is
     * the later end.
     */
    const lossAt = (): { at: number; canExtend: boolean } | null => {
      if (isSessionDead()) return { at: times.accessExpiresAt, canExtend: false };
      const end = times.sessionEndsAt;
      if (end === null) return null;
      if (times.accessExpiresAt > end) return { at: times.accessExpiresAt, canExtend: false };
      return { at: end, canExtend: !capped() };
    };

    const updateWarning = (nowS: number) => {
      const loss = lossAt();
      if (loss === null || loss.at - nowS > WARN_SECONDS) {
        if (useSessionStore.getState().isSessionExpiring) store.clearSessionExpiring();
        return;
      }
      const end = loss.at;
      const minutes = Math.max(0, Math.ceil((end - nowS) / 60));
      const s = useSessionStore.getState();
      const canExtend = loss.canExtend;
      if (!s.isSessionExpiring || s.minutesRemaining !== minutes || s.sessionEndsAt !== end || s.canExtend !== canExtend) {
        store.setSessionExpiring(minutes, end, canExtend);
      }
    };

    const evaluate = () => {
      const nowMs = Date.now();
      const nowS = nowMs / 1000;

      if (isSessionDead()) {
        // Nothing can renew now, but the access token keeps working until it
        // runs out: warn for that, then hand over to the session-expired dialog.
        if (nowS >= times.accessExpiresAt) {
          if (useSessionStore.getState().isSessionExpiring) store.clearSessionExpiring();
          reportSessionExpired();
        } else {
          updateWarning(nowS);
        }
        return;
      }

      updateWarning(nowS);

      if (
        renewalDue(nowS) &&
        userIsActive() &&
        nowMs - lastProactiveAt >= MIN_PROACTIVE_GAP_MS &&
        canRenewProactively(nowMs)
      ) {
        lastProactiveAt = nowMs;
        // Success brings a new user (this effect runs again); failure is
        // handled inside renewSession (back-off, or dead + banner). Nothing
        // here retries, so it cannot loop.
        void renewSession('proactive');
      }
    };

    const onActivity = () => {
      const now = Date.now();
      lastActivity = now;
      // Coming back to a tab whose token ran out: renew now rather than on the next tick.
      if (now - lastActivityCheck >= 5_000) {
        lastActivityCheck = now;
        evaluate();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onActivity();
    };

    for (const name of ACTIVITY_EVENTS) {
      window.addEventListener(name, onActivity, { passive: true, capture: true });
    }
    document.addEventListener('visibilitychange', onVisibility);

    evaluate();
    const interval = setInterval(evaluate, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      for (const name of ACTIVITY_EVENTS) {
        window.removeEventListener(name, onActivity, { capture: true });
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user]);
}
